/**
 * Smart adaptive formatting utilities.
 * Currency: picks best unit (B/M/K) based on magnitude.
 * Date: respects user-selected date format setting.
 */

import { format as dateFnsFormat } from 'date-fns';
import { safeGetItem, safeSetItem } from '@/lib/safeBrowser';

// ========================
// Date Format Configuration
// ========================

export type AppDateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';

const DATE_FNS_MAP: Record<AppDateFormat, string> = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
};

const SHORT_DATE_FNS_MAP: Record<AppDateFormat, string> = {
  'MM/DD/YYYY': 'MMM d',
  'DD/MM/YYYY': 'd MMM',
  'YYYY-MM-DD': 'MM-dd',
};

const FULL_DATE_FNS_MAP: Record<AppDateFormat, string> = {
  'MM/DD/YYYY': 'MMM d, yyyy',
  'DD/MM/YYYY': 'd MMM yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
};

let _activeDateFormat: AppDateFormat = (() => {
  return (safeGetItem('app-date-format') as AppDateFormat) || 'DD/MM/YYYY';
})();

export function setGlobalDateFormat(fmt: AppDateFormat) {
  _activeDateFormat = fmt;
  safeSetItem('app-date-format', fmt);
}

export function getGlobalDateFormat(): AppDateFormat {
  return _activeDateFormat;
}

/**
 * Format a date using the global user-selected format.
 * @param date The date to format
 * @param style 'default' (MM/DD/YYYY), 'short' (MMM d), 'full' (MMM d, yyyy), or a custom date-fns pattern
 * @param opts date-fns options like locale
 */
export function formatAppDate(
  date: Date | string | number,
  style: 'default' | 'short' | 'full' = 'default',
  opts?: { locale?: any }
): string {
  const d = date instanceof Date ? date : new Date(date);
  let pattern: string;
  switch (style) {
    case 'short': pattern = SHORT_DATE_FNS_MAP[_activeDateFormat]; break;
    case 'full': pattern = FULL_DATE_FNS_MAP[_activeDateFormat]; break;
    default: pattern = DATE_FNS_MAP[_activeDateFormat]; break;
  }
  return dateFnsFormat(d, pattern, opts);
}

/**
 * Format a date with time using the global format.
 */
export function formatAppDateTime(
  date: Date | string | number,
  opts?: { locale?: any }
): string {
  const d = date instanceof Date ? date : new Date(date);
  const datePattern = DATE_FNS_MAP[_activeDateFormat];
  return dateFnsFormat(d, `${datePattern} HH:mm`, opts);
}


// ========================
// Currency Configuration
// ========================

export type CurrencyCode = 'BRL' | 'USD' | 'EUR' | 'GBP';

interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  locale: string;
  symbolPosition: 'before' | 'after';
  thousandSeparator: string;
  decimalSeparator: string;
}

const CURRENCY_CONFIGS: Record<CurrencyCode, CurrencyConfig> = {
  BRL: { code: 'BRL', symbol: 'R$', locale: 'pt-BR', symbolPosition: 'before', thousandSeparator: '.', decimalSeparator: ',' },
  USD: { code: 'USD', symbol: '$', locale: 'en-US', symbolPosition: 'before', thousandSeparator: ',', decimalSeparator: '.' },
  EUR: { code: 'EUR', symbol: '€', locale: 'de-DE', symbolPosition: 'before', thousandSeparator: '.', decimalSeparator: ',' },
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB', symbolPosition: 'before', thousandSeparator: ',', decimalSeparator: '.' },
};

// Global currency state (singleton - works in both React components and data files)
let _activeCurrency: CurrencyCode = (() => {
  return (safeGetItem('app-currency') as CurrencyCode) || 'BRL';
})();

export function setGlobalCurrency(code: CurrencyCode) {
  _activeCurrency = code;
  safeSetItem('app-currency', code);
}

export function getGlobalCurrency(): CurrencyCode {
  return _activeCurrency;
}

export function getCurrencyConfig(code?: CurrencyCode): CurrencyConfig {
  return CURRENCY_CONFIGS[code || _activeCurrency];
}

export function getCurrencySymbol(code?: CurrencyCode): string {
  return getCurrencyConfig(code).symbol;
}

export const AVAILABLE_CURRENCIES: { code: CurrencyCode; label: string; symbol: string; flag: string }[] = [
  { code: 'BRL', label: 'Real (R$)', symbol: 'R$', flag: '🇧🇷' },
  { code: 'USD', label: 'Dollar ($)', symbol: '$', flag: '🇺🇸' },
  { code: 'EUR', label: 'Euro (€)', symbol: '€', flag: '🇪🇺' },
  { code: 'GBP', label: 'Pound (£)', symbol: '£', flag: '🇬🇧' },
];

// ========================
// Smart Currency Formatting
// ========================

function sym(): string {
  return getCurrencyConfig().symbol;
}

/**
 * Format a single currency value, picking the best unit automatically.
 */
export function formatSmartCurrency(
  value: number,
  opts: {
    decimals?: number;
    showSign?: boolean;
    compact?: boolean;
    currency?: CurrencyCode;
  } = {}
): string {
  const { showSign = false, compact = false } = opts;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : (showSign && value > 0 ? '+' : '');
  const s = opts.currency ? getCurrencyConfig(opts.currency).symbol : sym();

  if (abs >= 1_000_000_000) {
    const d = opts.decimals ?? (compact ? 1 : 1);
    return `${sign}${s}${(abs / 1_000_000_000).toFixed(d)}B`;
  }
  if (abs >= 1_000_000) {
    const d = opts.decimals ?? (compact ? 1 : 1);
    return `${sign}${s}${(abs / 1_000_000).toFixed(d)}M`;
  }
  if (abs >= 1_000) {
    const d = opts.decimals ?? 0;
    return `${sign}${s}${(abs / 1_000).toFixed(d)}K`;
  }
  if (abs === 0) return `${s}0`;
  return `${sign}${s}${abs.toFixed(opts.decimals ?? 0)}`;
}

/**
 * For chart Y-axis: determines a consistent unit based on the maximum
 * value in the dataset, then formats all values using that unit.
 */
export function createAxisFormatter(maxValue: number): (value: number) => string {
  const abs = Math.abs(maxValue);
  const s = sym();

  if (abs >= 1_000_000_000) {
    return (v: number) => `${s}${(v / 1_000_000_000).toFixed(1)}B`;
  }
  if (abs >= 1_000_000) {
    return (v: number) => `${s}${(v / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return (v: number) => `${s}${(v / 1_000).toFixed(0)}K`;
  }
  return (v: number) => `${s}${v.toFixed(0)}`;
}

/**
 * Full Intl.NumberFormat currency formatting (non-abbreviated).
 */
export function formatFullCurrency(value: number, code?: CurrencyCode): string {
  const cfg = getCurrencyConfig(code);
  return new Intl.NumberFormat(cfg.locale, {
    style: 'currency',
    currency: cfg.code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Formats a percentage value smartly. Input is ALREADY in percent units (e.g. 5.4 → "5.4%").
 */
export function formatSmartPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Formats a fraction (0..1) as a percent string (e.g. 0.054 → "5.4%").
 * Use this when the source returns ratios (win rate, coverage, lift).
 */
export function formatPercentFromFraction(value: number, decimals = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Formats a metric value based on its unit, ensuring max 1-2 decimal places.
 * - 'x' (coverage ratio) → 1 decimal (e.g. "1.0x")
 * - '%' → integer if whole, 1 decimal otherwise (e.g. "26%", "26.4%")
 * - 'd' (days) → integer + "d" (e.g. "45d")
 * - any other unit (currency) → smart currency formatting
 */
export function formatMetricValue(value: number, unit: string): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (unit === 'x') return `${value.toFixed(1)}x`;
  if (unit === '%') {
    return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
  }
  if (unit === 'd') return `${Math.round(value)}d`;
  return formatSmartCurrency(value);
}

/**
 * Formats a number with smart abbreviation (no currency symbol).
 */
export function formatSmartNumber(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${abs.toFixed(0)}`;
}
